<script lang="ts">
const textMinSizeForSnippet = 48
</script>
<script setup lang="ts">
const props = defineProps({
  file: null,
  containerWidth: null,
  containerHeight: null,
  fileType: null,
  flat: { type: Boolean },
  selected: { type: Boolean },
  shadowed: { type: Boolean },
  ghosted: { type: Boolean },
  hideStale: { type: Boolean }
})
const { isFileFresh } = useCache()
const { thumbnailPreviewImage } = useGlobals()
const extension = computed(() => {
  if (props.file.isLink())
    return "url"
  return props.file.extension()
})
const snippet = computed(() => props.file.textSnippet())
const stale = computed(() => !props.hideStale && !isFileFresh(props.file))
const fontSize = computed(() => px(Math.max(props.containerWidth / 80, 5)))
const safari = await isSafari()
const imageDimensions = computed(() => {
  let width = 143.4
  let height = 192
  if (props.flat || width === 0 || height === 0)
    return { width: props.containerWidth, height: props.containerHeight }

  // if our ratio is less than the containers (width / height), we anchor the size to the
  // container height, and scale the width accordingly, and vice versa.
  const ratio = width / height
  const containerRatio = props.containerWidth / props.containerHeight
  if (ratio < containerRatio) {
    height = props.containerHeight
    width = height * ratio
  }
  else {
    width = props.containerWidth
    height = width / ratio
  }
  return { width, height }
})


</script>

<template>
  <div
    v-if="flat"
    class="flat"
    :style="{
      width: px(imageDimensions.width),
      height: px(imageDimensions.height),
    }"
  >
    <svg
      v-if="fileType === 'unknown'"
      :width="64"
      :height="64"
      viewBox="0 0 85 84"
      class="unknown"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M59.2101 3.76562L79.2531 58.9258M59.2101 3.76562L5.58154 25.8594M59.2101 3.76562L55.6792 35.6172M59.2101 3.76562L31.0241 48.96M79.2531 58.9258L26.6102 80.2241M79.2531 58.9258L55.6792 35.6172M79.2531 58.9258L31.0241 48.96M26.6102 80.2241L5.58154 25.8594M26.6102 80.2241L55.6792 35.6172M26.6102 80.2241L31.0241 48.96M5.58154 25.8594L55.6792 35.6172M5.58154 25.8594L31.0241 48.96"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="4 4"
      />
    </svg>
    <div v-if="fileType === 'text' && imageDimensions.width > textMinSizeForSnippet" class="snippet">
      <div class="snippet-text" :style="{ fontSize }">
        {{ snippet }}
      </div>
    </div>
    <div v-else-if="fileType === 'text'" class="text-icon">
      <svg
        viewBox="0 0 144 192"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30.0981 63.4405V50.6094H113.5V63.4405M71.7993 50.6094V140.427M84.6304 140.427H58.9682"
          stroke="white"
          style="stroke:white;stroke-opacity:1;"
          stroke-width="12.8311"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
  <div
    v-else
    class="not-flat"
    :class="{
      [thumbnailPreviewImage]: true,
      safari,
      stale,
      selected,
      shadowed,
      ghosted,
    }"
    :style="{
      width: px(imageDimensions.width),
      height: px(imageDimensions.height),
    }"
  >
    <svg
      class="bg"
      viewBox="0 0 145 192"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M138.114 191.799H6.72168C3.76143 191.799 1.36168 189.4 1.36168 186.439V6C1.36168 3.03976 3.76143 0.64 6.72168 0.64H138.114C141.074 0.64 143.474 3.03976 143.474 6V186.439C143.474 189.4 141.074 191.799 138.114 191.799Z"
        fill="#262626"
        stroke="#3E3D41"
        style="fill:#262626;fill:color(display-p3 0.1491 0.1491 0.1491);fill-opacity:1;stroke:#3E3D41;stroke:color(display-p3 0.2431 0.2392 0.2549);stroke-opacity:1;"
        stroke-width="1.28"
      />
    </svg>
    <svg
      class="fg"
      viewBox="0 0 145 192"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M143.47 68.1669L144.109 68.1337L143.47 68.167C143.473 68.2276 143.474 68.2869 143.474 68.355V186.439C143.474 189.4 141.074 191.799 138.114 191.799H6.72168C3.76143 191.799 1.36168 189.4 1.36168 186.439V6C1.36168 3.03976 3.76143 0.64 6.72168 0.64H65.4893L65.4902 0.64C65.4902 0.64 65.4902 0.64 65.4902 0.64C65.499 0.640006 66.1462 0.640615 67.2041 0.680766C68.27 0.721216 69.7504 0.801712 71.4136 0.961766C74.7595 1.28374 78.7722 1.92157 81.6496 3.16489C84.1092 4.22764 86.8861 6.22859 89.1683 8.08592C91.4397 9.93452 93.1749 11.6025 93.5456 11.9634C93.5997 12.016 93.6555 12.0692 93.7133 12.1224L94.1466 11.6514L93.7133 12.1225L133.75 48.9527C133.806 49.0047 133.857 49.0533 133.908 49.1053C134.241 49.4428 135.507 50.7393 136.912 52.3409C138.326 53.9521 139.846 55.8341 140.711 57.3496C141.861 59.3621 142.544 61.8382 142.943 63.9489C143.34 66.0513 143.448 67.7451 143.47 68.1669Z"
        fill="#302F31"
        stroke="#3E3D41"
        style="fill:#302F31;fill:color(display-p3 0.1882 0.1843 0.1922);fill-opacity:1;stroke:#3E3D41;stroke:color(display-p3 0.2431 0.2392 0.2549);stroke-opacity:1;"
        stroke-width="1.28"
      />
    </svg>
    <svg
      v-if="fileType === 'unknown'"
      class="unknown"
      viewBox="0 0 145 192"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M93.4942 47.4688L119.149 118.074M93.4942 47.4688L24.8496 75.7488M93.4942 47.4688L88.9746 88.2387M93.4942 47.4688L57.4161 105.318M119.149 118.074L51.7663 145.336M119.149 118.074L88.9746 88.2387M119.149 118.074L57.4161 105.318M51.7663 145.336L24.8496 75.7488M51.7663 145.336L88.9746 88.2387M51.7663 145.336L57.4161 105.318M24.8496 75.7488L88.9746 88.2387M24.8496 75.7488L57.4161 105.318"
        stroke="#4F4C54"
        style="stroke:#4F4C54;stroke:color(display-p3 0.3098 0.2980 0.3294);stroke-opacity:1;"
        stroke-width="2.56"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="5.12 5.12"
      />
    </svg>
    <div v-if="fileType === 'text' && imageDimensions.width > textMinSizeForSnippet" class="snippet">
      <div class="snippet-text" :style="{ fontSize }">
        {{ snippet }}
      </div>
    </div>
    <div v-else-if="fileType === 'text'" class="text-icon">
      <svg
        viewBox="0 0 144 192"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30.0981 63.4405V50.6094H113.5V63.4405M71.7993 50.6094V140.427M84.6304 140.427H58.9682"
          stroke="white"
          style="stroke:white;stroke-opacity:1;"
          stroke-width="12.8311"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <svg
      class="curl"
      viewBox="0 0 145 192"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(1.0)">
        <path
          d="M93.0228 11.5192C92.3338 10.8475 84.9248 4.09608 79.9442 2.27122C79.6539 2.16334 78.9558 1.91545 78.4858 1.78697C78.0157 1.65849 76.9859 1.39108 76.5298 1.27344H136.868C139.492 1.27344 141.618 3.40008 141.618 6.02343V60.7597C141.62 60.7667 141.619 60.771 141.618 60.7744V60.7597C141.609 60.7107 141.531 60.5299 141.235 59.6379C140.949 58.7783 140.388 57.6473 140.139 57.1902C138.326 54.0185 133.998 49.2821 133.368 48.6439C133.317 48.5922 133.267 48.5444 133.214 48.4951L93.1519 11.6416C93.1072 11.6005 93.0663 11.5616 93.0228 11.5192Z"
          fill="#262626"
          style="fill:#262626;fill:color(display-p3 0.1490 0.1490 0.1490);fill-opacity:1;"
        />
      </g>
      <template v-if="imageDimensions.width > textMinSizeForSnippet">
        <text
          v-if="extension && extension.length === 1"
          x="117"
          y="33"
          class="extension"
          style="font-size: 32px;"
        >
          {{ extension.toUpperCase() }}
        </text>
        <text
          v-else-if="extension && extension.length === 2"
          x="107"
          y="27"
          class="extension"
          style="font-size: 25px;"
        >
          {{ extension.toUpperCase() }}
        </text>
        <text
          v-else-if="extension && extension.length === 3"
          x="99"
          y="24"
          class="extension"
          style="font-size: 21px;"
        >
          {{ extension.toUpperCase() }}
        </text>
        <text
          v-else-if="extension && extension.length > 3"
          x="98"
          y="20"
          class="extension"
          fill="white"
          style="font-size: 16px;"
        >
          {{ extension.toUpperCase().slice(extension.length - 4) }}
        </text>
      </template>
      <g clip-path="url(#clip0_11268_213177)">
        <g filter="url(#filter0_dd_11268_213177)">
          <path
            d="M81.1597 2.26953C81.1597 2.26953 81.2137 2.27353 81.3091 2.32546C86.2871 4.22816 93.5562 10.8525 94.2383 11.5175C94.2818 11.5599 94.3227 11.5989 94.3674 11.64L134.429 48.4935C134.483 48.5427 134.532 48.5905 134.584 48.6423C135.209 49.276 139.482 53.9512 141.316 57.1222C141.348 57.1652 141.354 57.1885 141.354 57.1885C141.341 57.1665 141.329 57.1443 141.316 57.1222C141.056 56.7766 139.071 55.1542 124.447 53.9974C114.344 53.1982 98.8282 52.834 91.0152 53.0372C90.5668 53.0489 90.1056 53.0769 89.6445 53.1164C87.1847 53.3271 84.8789 50.9598 84.9884 48.4935C85.4309 38.5315 85.4226 38.8246 84.9884 23.3973C84.4764 5.20646 81.9523 2.67572 81.3091 2.32546C81.259 2.30634 81.2092 2.28769 81.1597 2.26953Z"
            fill="url(#paint0_linear_11268_213177)"
            style=""
          />
          <path
            d="M81.1597 2.26953C81.1597 2.26953 81.2137 2.27353 81.3091 2.32546C86.2871 4.22816 93.5562 10.8525 94.2383 11.5175C94.2818 11.5599 94.3227 11.5989 94.3674 11.64L134.429 48.4935C134.483 48.5427 134.532 48.5905 134.584 48.6423C135.209 49.276 139.482 53.9512 141.316 57.1222C141.348 57.1652 141.354 57.1885 141.354 57.1885C141.341 57.1665 141.329 57.1443 141.316 57.1222C141.056 56.7766 139.071 55.1542 124.447 53.9974C114.344 53.1982 98.8282 52.834 91.0152 53.0372C90.5668 53.0489 90.1056 53.0769 89.6445 53.1164C87.1847 53.3271 84.8789 50.9598 84.9884 48.4935C85.4309 38.5315 85.4226 38.8246 84.9884 23.3973C84.4764 5.20646 81.9523 2.67572 81.3091 2.32546C81.259 2.30634 81.2092 2.28769 81.1597 2.26953Z"
            fill="url(#paint1_linear_11268_213177)"
            style=""
          />
        </g>
      </g>
      <defs>
        <filter
          id="filter0_dd_11268_213177"
          x="64.147"
          y="-7.19275"
          width="103.941"
          height="84.0825"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="12.1519" dy="5.12" />
          <feGaussianBlur stdDeviation="7.29114" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.0823529 0 0 0 0 0.0862745 0 0 0 0 0.105882 0 0 0 0.3 0" />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_11268_213177"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="-2.43038" dy="5.12" />
          <feGaussianBlur stdDeviation="7.29114" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.0823529 0 0 0 0 0.0862745 0 0 0 0 0.105882 0 0 0 0.3 0" />
          <feBlend
            mode="normal"
            in2="effect1_dropShadow_11268_213177"
            result="effect2_dropShadow_11268_213177"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect2_dropShadow_11268_213177"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_11268_213177"
          x1="106.368"
          y1="14.8132"
          x2="87.288"
          y2="54.0702"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#3F3E42" style="stop-color:#3F3E42;stop-color:color(display-p3 0.2471 0.2431 0.2588);stop-opacity:1;" />
          <stop
            offset="0.268103"
            stop-color="#434246"
            style="stop-color:#434246;stop-color:color(display-p3 0.2627 0.2588 0.2745);stop-opacity:1;"
          />
          <stop
            offset="0.550856"
            stop-color="#464549"
            style="stop-color:#464549;stop-color:color(display-p3 0.2733 0.2689 0.2866);stop-opacity:1;"
          />
          <stop
            offset="0.736282"
            stop-color="#434246"
            style="stop-color:#434246;stop-color:color(display-p3 0.2637 0.2597 0.2757);stop-opacity:1;"
          />
          <stop
            offset="1"
            stop-color="#3F3E42"
            style="stop-color:#3F3E42;stop-color:color(display-p3 0.2471 0.2431 0.2588);stop-opacity:1;"
          />
        </linearGradient>
        <linearGradient
          id="paint1_linear_11268_213177"
          x1="115.172"
          y1="37.0608"
          x2="151.49"
          y2="54.8796"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            stop-color="#3E3C40"
            stop-opacity="0"
            style="stop-color:none;stop-opacity:0;"
          />
          <stop
            offset="1"
            stop-color="#3E3C40"
            style="stop-color:#3E3C40;stop-color:color(display-p3 0.2431 0.2353 0.2510);stop-opacity:1;"
          />
        </linearGradient>
        <clipPath id="clip0_11268_213177">
          <rect
            width="143.392"
            height="192"
            fill="white"
            style="fill:white;fill-opacity:1;"
            transform="translate(0.72168)"
          />
        </clipPath>
      </defs>
    </svg>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.extension
  fill: $global-t-tertiary
  @apply monospace font-semibold

.flat
  @apply bg-[#{$page-file-bg-file}] flex items-center justify-center overflow-hidden relative
  .unknown
    @apply text-[#{$page-file-b-broken-icon}]
  .snippet
    @apply absolute h-full p-2.0 overflow-hidden flex-none pointer-events-none
    @apply font-mono text-[#{global-t-default}]
  .snippet-text
    @apply overflow-hidden h-full w-full whitespace-break-spaces break-all text-[7px]

.not-flat
  @apply w-full h-full relative
  // On safari, the default filter seems to interfere with the hover/selection filter color,
  // causing weird artifacts. So, we detect if the user is on safari and remove this default
  // filter in that instance.
  // TODO: tauri desktop seems to not support our shim for detecting safari
  // &:not(.safari)
  //   filter: drop-shadow(-4px 2px 4px #10101040)
  &.stale
    .bg
      @include alphaShadow(var(--page-b-offline), 2px, drop-shadow(-4px 2px 4px #10101040))
  &.ghosted
    .bg
      @include alphaShadow(var(--global-blue-accent), 2px, drop-shadow(-4px 2px 4px #10101040))
  &.shadowed:hover, &.selected
    .bg
      @include alphaShadow(var(--global-blue-primary), 1px, drop-shadow(-4px 2px 4px #10101040))

  svg
    @apply max-w-full max-h-full absolute
    .extension
      fill: $global-t-tertiary
      @apply font-mono font-bold tracking-wide
  .snippet
    @apply absolute top-0 left-0 right-0 bottom-0 p-[5%]
    @apply overflow-hidden pointer-events-none
    @apply font-mono text-[#{global-t-default}]
  .snippet-text
    @apply overflow-hidden h-full w-full whitespace-break-spaces break-all text-[7px]
</style>
